import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { MaskLine } from "../Utility/Motion";
import { getLenis } from "../Utility/scroll";
import { AWARDS, AWARDS_INTERVAL } from "../Data/Awardsdata";
import { pick } from "../Data/Developersdata";

const EASE = [0.76, 0, 0.24, 1];

const Chevron = ({ flip }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true"
    style={flip ? { transform: "scaleX(-1)" } : undefined}>
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

// عرض كارت واحد + المسافة بينه وبين اللي بعده
const stepOf = (el) => {
  const card = el && el.querySelector(".award");
  if (!card) return 0;
  const cs = getComputedStyle(el);
  const gap = parseFloat(cs.columnGap) || parseFloat(cs.gap) || 0;
  return card.getBoundingClientRect().width + gap;
};
const atEnd = (el) => !el || Math.abs(el.scrollLeft) + el.clientWidth >= el.scrollWidth - 4;
const atStart = (el) => !el || Math.abs(el.scrollLeft) <= 4;

const text = (v, lang) => (v && typeof v === "object" ? pick(v, lang) : v || "");

/**
 * التكريمات: سلايدر بيتحرك لوحده كل كام ثانية (AWARDS_INTERVAL في Data/Awardsdata.js)
 * - بيقف لما الماوس يقف عليه أو الزائر يلمسه أو يكون برّه الشاشة
 * - بيتسحب بالصابع على الموبايل، وفيه أسهم، والضغط على الصورة بيكبّرها
 */
export const MainAwards = ({ id = "awards" }) => {
  const { t, lang, isRTL } = useLang();
  const a = t.awards;
  const reduce = useReducedMotion();
  const [failed, setFailed] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [active, setActive] = useState(0);
  const track = useRef(null);
  const head = useRef(null);
  const wrap = useRef(null);
  const paused = useRef(false);
  const resumeAt = useRef(0);
  const headIn = useInView(head, { amount: 0.4 });
  const inView = useInView(wrap, { amount: 0.25 });

  const items = AWARDS.map((it, i) => ({ ...it, i })).filter((it) => it.img && !failed.has(it.i));
  const n = items.length;

  const move = useCallback(
    (dir) => {
      const el = track.current;
      if (!el) return;
      const sign = isRTL ? -1 : 1;
      if (dir > 0 && atEnd(el)) el.scrollTo({ left: 0, behavior: "smooth" });
      else if (dir < 0 && atStart(el)) el.scrollTo({ left: sign * el.scrollWidth, behavior: "smooth" });
      else el.scrollBy({ left: sign * dir * stepOf(el), behavior: "smooth" });
    },
    [isRTL]
  );

  // الكارت الحالي (للنقط تحت السلايدر)
  const onScroll = () => {
    const s = stepOf(track.current);
    if (!s) return;
    setActive(Math.min(n - 1, Math.round(Math.abs(track.current.scrollLeft) / s)));
  };

  const hold = (ms = 6000) => {
    resumeAt.current = Date.now() + ms;
  };

  // الحركة التلقائية
  useEffect(() => {
    if (reduce || n < 2 || !inView || open != null) return undefined;
    const id = setInterval(() => {
      if (paused.current || document.hidden || Date.now() < resumeAt.current) return;
      const el = track.current;
      if (el && el.scrollWidth <= el.clientWidth + 4) return; // كله ظاهر، مفيش داعي للحركة
      move(1);
    }, AWARDS_INTERVAL);
    return () => clearInterval(id);
  }, [reduce, n, inView, open, move]);

  // الصورة الكبيرة: نوقف سكرول الصفحة ونقفل بـ Esc ونتنقل بالأسهم
  useEffect(() => {
    if (open == null) return undefined;
    const lenis = getLenis();
    if (lenis) lenis.stop();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + (isRTL ? -1 : 1) + n) % n);
      if (e.key === "ArrowLeft") setOpen((o) => (o + (isRTL ? 1 : -1) + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (lenis) lenis.start();
    };
  }, [open, n, isRTL]);

  if (!n) return null;

  const caption = (it) => [text(it.title, lang), text(it.by, lang), it.year].filter(Boolean).join(" · ");
  const label = (k) => a.slide.replace("{n}", k + 1).replace("{total}", n);
  const cur = open != null ? items[open] : null;

  return (
    <section className="awards" id={id} ref={wrap} aria-roledescription="carousel" aria-label={a.title.join(" ")}>
      <div className="awards__head" ref={head}>
        <div>
          <p className="awards__label">{a.label}</p>
          <h2 className="awards__title">
            {a.title.map((line, i) => (
              <MaskLine key={line} show={headIn} delay={i * 0.14}>
                {line}
              </MaskLine>
            ))}
          </h2>
        </div>
        <div className="awards__side">
          <p className="awards__sub">{a.sub}</p>
          {n > 1 && (
            <div className="awards__nav">
              <button type="button" className="awards__arrow" aria-label={a.prev} onClick={() => { hold(); move(-1); }}>
                <Chevron flip={isRTL} />
              </button>
              <button type="button" className="awards__arrow" aria-label={a.next} onClick={() => { hold(); move(1); }}>
                <Chevron flip={!isRTL} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className="awards__track"
        ref={track}
        onScroll={onScroll}
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
        onPointerDown={() => hold(8000)}
        onFocus={() => (paused.current = true)}
        onBlur={() => (paused.current = false)}
      >
        {items.map((it, k) => (
          <motion.figure
            key={it.img}
            className="award"
            role="group"
            aria-roledescription="slide"
            aria-label={label(k)}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: Math.min(k, 4) * 0.08, ease: EASE }}
          >
            <button type="button" className="award__frame" onClick={() => setOpen(k)} aria-label={`${a.open}: ${caption(it) || label(k)}`}>
              <img
                className="award__bg"
                src={it.img}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
              />
              <img
                className="award__img"
                src={it.img}
                alt={caption(it) || label(k)}
                loading="lazy"
                decoding="async"
                onLoad={(e) => e.currentTarget.classList.add("is-loaded")}
                onError={() => setFailed((f) => new Set(f).add(it.i))}
              />
            </button>
            {caption(it) && <figcaption className="award__cap">{caption(it)}</figcaption>}
          </motion.figure>
        ))}
      </div>

      {n > 1 && (
        <div className="awards__dots" aria-hidden="true">
          {items.map((it, k) => (
            <i key={it.img} className={k === active ? "is-on" : ""} />
          ))}
        </div>
      )}

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {cur && (
              <motion.div
                className="awards__box"
                role="dialog"
                aria-modal="true"
                aria-label={caption(cur) || label(open)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                onClick={() => setOpen(null)}
                data-lenis-prevent
              >
                <motion.figure
                  key={cur.img}
                  className="awards__boxfig"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={cur.img} alt={caption(cur) || label(open)} />
                  <figcaption>
                    <span>{caption(cur)}</span>
                    <small>{label(open)}</small>
                  </figcaption>
                </motion.figure>
                <button type="button" className="awards__close" aria-label={a.close} onClick={() => setOpen(null)}>
                  ×
                </button>
                {n > 1 && (
                  <>
                    <button
                      type="button"
                      className="awards__arrow awards__arrow--box awards__arrow--prev"
                      aria-label={a.prev}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen((o) => (o - 1 + n) % n);
                      }}
                    >
                      <Chevron flip={isRTL} />
                    </button>
                    <button
                      type="button"
                      className="awards__arrow awards__arrow--box awards__arrow--next"
                      aria-label={a.next}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen((o) => (o + 1) % n);
                      }}
                    >
                      <Chevron flip={!isRTL} />
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </section>
  );
};
